import { FileText, Type } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ProposalDocumentLayoutPage } from "../../types";

interface BuilderCoverLetterSettingsProps {
  page: ProposalDocumentLayoutPage;
  readOnly?: boolean;
  onChange: (updates: Partial<ProposalDocumentLayoutPage>) => void;
  className?: string;
}

type CoverLetterSettings = Record<string, string | boolean | number | null>;

function getSettings(page: ProposalDocumentLayoutPage): CoverLetterSettings {
  return page.settings && typeof page.settings === "object"
    ? (page.settings as CoverLetterSettings)
    : {};
}

function getStringSetting(
  settings: CoverLetterSettings,
  key: string,
  fallback = ""
) {
  const value = settings[key];
  return typeof value === "string" ? value : fallback;
}

function getBooleanSetting(
  settings: CoverLetterSettings,
  key: string,
  fallback: boolean
) {
  const value = settings[key];
  return typeof value === "boolean" ? value : fallback;
}

function updateSetting(
  settings: CoverLetterSettings,
  key: string,
  value: string | boolean | number | null
) {
  return {
    settings: {
      ...settings,
      [key]: value,
    },
  };
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
      <div className="border-b border-white/10 px-4 py-3">
        <p className="text-sm font-medium text-white/88">{title}</p>
        {description ? (
          <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
        ) : null}
      </div>

      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

function FieldLabel({
  label,
  icon,
  hint,
}: {
  label: string;
  icon?: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="mb-2">
      <label className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-white/35">
        {icon}
        {label}
      </label>
      {hint ? <p className="mt-1 text-xs text-white/35">{hint}</p> : null}
    </div>
  );
}

function ToggleField({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/20 p-4">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-white/20 bg-black/20 text-white"
      />

      <div>
        <p className="text-sm font-medium text-white/86">{label}</p>
        <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
      </div>
    </label>
  );
}

export function BuilderCoverLetterSettings({
  page,
  readOnly = false,
  onChange,
  className,
}: BuilderCoverLetterSettingsProps) {
  const settings = getSettings(page);

  const title = getStringSetting(settings, "title", "Cover Letter");
  const introText = getStringSetting(settings, "introText");
  const bodySource = getStringSetting(settings, "bodySource", "brand_settings");
  const closingText = getStringSetting(settings, "closingText");
  const showCompanySignature = getBooleanSetting(
    settings,
    "showCompanySignature",
    true
  );
  const showDate = getBooleanSetting(settings, "showDate", true);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-white/10 bg-black/20",
        className
      )}
    >
      <div className="border-b border-white/10 bg-gradient-to-r from-violet-500/10 via-fuchsia-500/6 to-transparent px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg border border-white/10 bg-white/[0.06] p-2 text-white/65">
            <FileText className="h-4 w-4" />
          </div>

          <div className="min-w-0">
            <p className="text-sm font-medium text-white/88">
              Cover Letter Settings
            </p>
            <p className="mt-1 text-xs leading-5 text-white/40">
              Adjust the opening framing, body source, and signoff behavior that
              shape the cover letter page on the stage.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <SectionCard
          title="Letter framing"
          description="Set the visible title and opening message at the top of the cover letter."
        >
          <div>
            <FieldLabel
              label="Page title"
              icon={<Type className="h-4 w-4" />}
              hint="Visible heading for the cover letter page."
            />
            <input
              value={title}
              onChange={(e) =>
                onChange(updateSetting(settings, "title", e.target.value))
              }
              disabled={readOnly}
              placeholder="Cover Letter"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>

          <div>
            <FieldLabel
              label="Intro text"
              hint="Optional short lead-in before the main body of the letter."
            />
            <textarea
              rows={4}
              value={introText}
              onChange={(e) =>
                onChange(updateSetting(settings, "introText", e.target.value))
              }
              disabled={readOnly}
              placeholder="Optional short opening before the main cover letter body"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Body source"
          description="Choose where the main cover letter content should come from."
        >
          <div>
            <FieldLabel
              label="Letter body source"
              hint="Use shared language, template defaults, or custom page-level content."
            />
            <select
              value={bodySource}
              onChange={(e) =>
                onChange(updateSetting(settings, "bodySource", e.target.value))
              }
              disabled={readOnly}
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            >
              <option value="brand_settings" className="bg-[#111]">
                Brand Setup cover letter
              </option>
              <option value="template_default" className="bg-[#111]">
                Template default content
              </option>
              <option value="custom" className="bg-[#111]">
                Custom per-page content
              </option>
            </select>
          </div>

          <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
            <p className="text-xs leading-5 text-white/40">
              Use Brand Setup for shared company language, Template Default for
              reusable template copy, or Custom when this page should intentionally
              diverge.
            </p>
          </div>
        </SectionCard>

        <SectionCard
          title="Closing and signoff"
          description="Control the closing note and the details shown at the bottom of the letter."
        >
          <div>
            <FieldLabel
              label="Closing text"
              hint="Optional final note, CTA, or closing paragraph."
            />
            <textarea
              rows={4}
              value={closingText}
              onChange={(e) =>
                onChange(updateSetting(settings, "closingText", e.target.value))
              }
              disabled={readOnly}
              placeholder="Optional closing note or CTA"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>

          <ToggleField
            label="Show company signoff"
            description="Display the business signoff or signature area at the bottom of the cover letter."
            checked={showCompanySignature}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showCompanySignature", checked))
            }
          />

          <ToggleField
            label="Show date"
            description="Include the proposal date at the top of the cover letter page."
            checked={showDate}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showDate", checked))
            }
          />
        </SectionCard>
      </div>
    </div>
  );
}